import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import businessService, { 
  Business, 
  BusinessStats, 
  BusinessAudit, 
  BusinessUser 
} from '../../services/businessService';
import { formatDate } from '../../utils/dateUtils';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Business as BusinessIcon,
  People,
  Assessment,
  History,
  Phone,
  Email,
  LocationOn,
  Person,
  Description as FormIcon
} from '@mui/icons-material';
import BusinessForm from './BusinessForm';
import BusinessFormsList from './components/BusinessFormsList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
};

const BusinessDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [business, setBusiness] = useState<Business | null>(null);
  const [stats, setStats] = useState<BusinessStats | null>(null);
  const [audits, setAudits] = useState<BusinessAudit[]>([]);
  const [users, setUsers] = useState<BusinessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    loadBusinessData();
  }, [id]);

  const loadBusinessData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Cargar datos del negocio
      const businessData = await businessService.getById(Number(id));
      setBusiness(businessData);

      // Cargar estadísticas
      try {
        const statsData = await businessService.getStats(Number(id));
        setStats(statsData);
      } catch (err) {
        console.error('Error cargando estadísticas:', err);
      }

      // Cargar auditorías
      try {
        const auditsData = await businessService.getAudits(Number(id));
        // El backend puede devolver { data: [], ... } o directamente el array
        const auditsArray = Array.isArray(auditsData) 
          ? auditsData 
          : (auditsData as any)?.data || [];
        setAudits(auditsArray);
      } catch (err) {
        console.error('Error cargando auditorías:', err);
        setAudits([]); // Asegurar que siempre sea un array
      }

      // Cargar usuarios asignados
      try {
        const usersData = await businessService.getUsers(Number(id));
        // El backend puede devolver { data: [], ... } o directamente el array
        const usersArray = Array.isArray(usersData) 
          ? usersData 
          : (usersData as any)?.data || [];
        setUsers(usersArray);
      } catch (err) {
        console.error('Error cargando usuarios:', err);
        setUsers([]); // Asegurar que siempre sea un array
      }

    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al cargar negocio');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditSuccess = () => {
    setEditOpen(false);
    loadBusinessData();
  };

  const getStatusColor = (estado: string) => {
    switch (estado) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (estado: string) => {
    switch (estado) {
      case 'completed': return 'Completada';
      case 'in_progress': return 'En Progreso';
      case 'draft': return 'Borrador';
      default: return estado;
    }
  };

  const getRolSx = (rol: string) => {
    switch (rol) {
      case 'admin':
        return { bgcolor: '#000000', color: '#FFEB5D', fontWeight: 600 };
      case 'manager':
        return { bgcolor: '#FFEB5D', color: '#000000', fontWeight: 600 };
      case 'auditor':
        return { bgcolor: '#9E9E9E', color: '#000000', fontWeight: 600 };
      default:
        return {};
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !business) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {error || 'No se encontró el negocio'}
        </Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/business')}
          sx={{ mt: 2 }}
        >
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} gap={1} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexShrink={1} minWidth={0}>
            <IconButton onClick={() => navigate('/business')} size="small">
              <ArrowBack />
            </IconButton>
            <BusinessIcon fontSize="large" color="primary" sx={{ display: { xs: 'none', sm: 'block' } }} />
            <Box minWidth={0} flexShrink={1}>
              <Typography variant="h5" component="h1" sx={{ 
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                wordBreak: 'break-word'
              }}>
                {business.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap>
                {business.industry || 'Sin rubro especificado'}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1} flexShrink={0}>
            <Chip
              label={business.active ? 'Activo' : 'Inactivo'}
              color={business.active ? 'success' : 'default'}
              size="small"
            />
            <Button
              variant="contained"
              startIcon={<Edit sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />}
              onClick={() => setEditOpen(true)}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                py: { xs: 0.75, sm: 1 },
                px: { xs: 1.5, sm: 2 },
              }}
            >
              Editar
            </Button>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Información de contacto */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {business.address || 'Sin dirección'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Phone fontSize="small" color="action" />
              <Typography variant="body2">
                {business.phone || 'Sin teléfono'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Email fontSize="small" color="action" />
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {business.email || 'Sin email'}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Person fontSize="small" color="action" />
              <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                {business.manager_name || 'Sin contacto'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Total Auditorías
                </Typography>
                <Typography variant="h4">
                  {stats.total_audits}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Completadas
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completed_audits}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Promedio Cumplimiento
                </Typography>
                <Typography variant="h4" color="primary">
                  {(Number(stats.average_compliance) || 0).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom variant="body2">
                  Usuarios Asignados
                </Typography>
                <Typography variant="h4">
                  {stats.total_assigned_users}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 64 },
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 }
            }
          }}
        >
          <Tab icon={<History />} label="Auditorías" iconPosition="start" />
          <Tab icon={<People />} label="Usuarios" iconPosition="start" />
          <Tab icon={<FormIcon />} label="Formularios" iconPosition="start" />
          <Tab icon={<Assessment />} label="Estadísticas" iconPosition="start" />
        </Tabs>

        {/* Tab: Auditorías */}
        <TabPanel value={tabValue} index={0}>
          {audits.length === 0 ? (
            <Alert severity="info">No hay auditorías registradas</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Formulario</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Auditor</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Fecha Inicio</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Fecha Fin</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right">Cumplimiento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audits.map((audit) => (
                    <TableRow key={audit.id}>
                      <TableCell>{audit.form_name}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{audit.user_name}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {formatDate(audit.start_date, business?.timezone)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {audit.end_date 
                          ? formatDate(audit.end_date, business?.timezone)
                          : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(audit.status)}
                          color={getStatusColor(audit.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {audit.compliance_percentage 
                          ? `${audit.compliance_percentage}%`
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab: Usuarios */}
        <TabPanel value={tabValue} index={1}>
          {users.length === 0 ? (
            <Alert severity="info">No hay usuarios asignados</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                    <TableCell>Rol</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          size="small"
                          sx={getRolSx(user.role)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* Tab: Formularios */}
        <TabPanel value={tabValue} index={2}>
          <BusinessFormsList
            businessId={Number(id)}
            businessName={business.name}
            businessTimezone={business.timezone}
          />
        </TabPanel>

        {/* Tab: Estadísticas */}
        <TabPanel value={tabValue} index={3}>
          {stats ? (
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Resumen de Auditorías
                    </Typography>
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        Completadas: {stats.completed_audits} de {stats.total_audits}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pendientes: {stats.pending_audits}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={2}>
                        Promedio de cumplimiento: {(Number(stats.average_compliance) || 0).toFixed(1)}%
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Equipo
                    </Typography>
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary">
                        Total de usuarios asignados: {stats.total_assigned_users}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">No hay estadísticas disponibles</Alert>
          )}
        </TabPanel>
      </Paper>

      {/* Modal de edición */}
      <BusinessForm
        open={editOpen}
        business={business}
        onClose={() => setEditOpen(false)}
        onSuccess={handleEditSuccess}
      />
    </Box>
  );
};

export default BusinessDetail;
